import React from 'react';
import { StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { theme } from '@sg/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { useIsLoggedIn } from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useFeatureFlag, useFeatureFlagAsync } from '@order/LaunchDarkly';

import type { ChallengesScreenProps } from '../../navigation/AppNavigation.props';
import { ChallengesScreenContent } from './subcomponents';

// ────────────────────────────────────────────────────────────────────────────────

export const ChallengesScreen = ({ navigation }: ChallengesScreenProps) => {
  const { value: isChallengesEnabled, isStale: isLoadingFeatureFlag } =
    useFeatureFlagAsync('OPS-challenges-enabled', {
      listenForChanges: true,
    });
  const { navigate } = useNavigation();
  const { isFetchingCustomer } = useCustomer();
  const isLoggedIn = useIsLoggedIn();

  const shouldUseAuthFlowRework = useFeatureFlag(
    'cels-763-short-rework-the-authentication-redirect-flow-enabled',
  );
  const { startSignIn } = useAuthFlow();

  // ─────────────────────────────────────────────────────────────────

  const handlePressSignIn = React.useCallback(() => {
    if (shouldUseAuthFlowRework) {
      navigate('Auth', {
        screen: 'Login',
        params: { redirect: 'challenges' },
      });

      return;
    }

    startSignIn('challenges');
  }, [navigate, shouldUseAuthFlowRework, startSignIn]);

  // ─────────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (isLoadingFeatureFlag || isChallengesEnabled) return;
    navigation.navigate('HomeTab', { screen: 'Home' });
  }, [navigation, isLoadingFeatureFlag, isChallengesEnabled]);

  const isLoading = isFetchingCustomer || isLoadingFeatureFlag;

  // ─────────────────────────────────────────────────────────────────

  if (!isChallengesEnabled && !isLoadingFeatureFlag) {
    return null;
  }

  return (
    <PageWrapper
      testID="challenges.root.container"
      style={styles.pageWrapper}
      withHeaderTitle
    >
      <ChallengesScreenContent
        testID="challenges"
        isLoading={isLoading}
        isSignedIn={isLoggedIn}
        onPressSignIn={handlePressSignIn}
      />
    </PageWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pageWrapper: {
    flexGrow: 1,
    backgroundColor: theme.colors.KHAKI_3,
  },
});
